import {useState} from 'react';
import {Link, NavLink, useLocation} from 'react-router-dom';
import {ArrowRight, ArrowUpRight, Menu, Phone, X} from 'lucide-react';
import {cn} from '@common-web/lib/utils';
import {POSTHOG_UNMASK_CLASS} from '@common-web/lib/analytics';
import {useEnrollGroup} from '@client/pages/public/enroll/enrollContext';
import {useLinkHref} from '@client/pages/public/enroll/enrollPreview';
import {SiteWordmark} from '@client/pages/public/enroll/components/SiteWordmark';
import {STEP} from '@client/pages/public/enroll/enrollModel';
import conciergeLogo from '@client/pages/public/enroll/assets/concierge-horizontal.png';

type NavItem =
  | {kind: 'route'; to: string; label: string; end?: boolean}
  | {kind: 'guide'; label: string};

const NAV_ITEMS: NavItem[] = [
  {kind: 'route', to: '', label: 'Home', end: true},
  {kind: 'route', to: 'faq', label: 'FAQ'},
  {kind: 'guide', label: 'Benefits Guide'},
  {kind: 'route', to: 'how-to-enroll', label: 'How to Enroll'},
];

const DESKTOP_LINK =
  'inline-flex items-center gap-1 whitespace-nowrap border-b-2 px-3 py-2 text-sm font-semibold transition-colors';
const MOBILE_LINK = 'flex items-center gap-1 border-b border-neutral-100 py-3 text-base font-semibold';
const LINK_INACTIVE = 'border-transparent text-neutral-500 hover:text-[var(--enroll-ink)]';
const LINK_ACTIVE = 'border-[var(--enroll-accent)] text-[var(--enroll-ink)]';

const STEP_PATHS = new Set<string>(Object.values(STEP));

function NavLinks({base, mobile, onNavigate}: {base: string; mobile?: boolean; onNavigate?: () => void}) {
  const group = useEnrollGroup();
  const linkHref = useLinkHref();
  const linkBase = mobile ? MOBILE_LINK : DESKTOP_LINK;
  return (
    <>
      {NAV_ITEMS.map((item) => {
        if (item.kind === 'guide') {
          if (!group.comparisonPdfUrl) return null;
          return (
            <a
              key='guide'
              href={linkHref(group.comparisonPdfUrl)}
              target='_blank'
              rel='noreferrer'
              onClick={onNavigate}
              className={cn(linkBase, LINK_INACTIVE)}
            >
              {item.label}
              <ArrowUpRight className='h-3.5 w-3.5' />
            </a>
          );
        }
        return (
          <NavLink
            key={item.to}
            to={item.to ? `${base}/${item.to}` : base}
            end={item.end}
            onClick={onNavigate}
            className={({isActive}) => cn(linkBase, isActive ? LINK_ACTIVE : LINK_INACTIVE)}
          >
            {item.label}
          </NavLink>
        );
      })}
    </>
  );
}

export function Header() {
  const group = useEnrollGroup();
  const location = useLocation();
  const base = `/${group.slug}`;
  const lastSegment = location.pathname.split('/').filter(Boolean).pop() ?? '';
  // True on the multi-step enroll pages (medical/dental/checkout/...), false on landing/info pages.
  const isInFlow = STEP_PATHS.has(lastSegment);
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <header className={cn('border-b border-neutral-200 bg-white', POSTHOG_UNMASK_CLASS)}>
      <div className='mx-auto flex max-w-7xl items-center justify-between gap-4 px-6 py-3'>
        <Link to={base} className='flex flex-col items-start gap-1' onClick={() => setMenuOpen(false)}>
          {group.logoUrl ? (
            <img src={group.logoUrl} alt={group.name} className='h-10 w-auto sm:h-12' />
          ) : (
            <SiteWordmark name={group.name} primaryColor={group.primaryColor} />
          )}
          <div className='flex items-center gap-1.5 pl-1'>
            <span className='text-[9px] font-bold uppercase tracking-[0.18em] text-neutral-500'>
              Powered by
            </span>
            <img
              src={conciergeLogo}
              alt='Concierge Third Party Administrator'
              width={494}
              height={96}
              className='h-5 w-auto opacity-80'
            />
          </div>
        </Link>

        <div className='flex items-center gap-1'>
          {/* Inline nav on desktop; on mobile it moves into the dropdown below. */}
          <nav className='hidden items-center gap-1 md:flex'>
            <NavLinks base={base} />
          </nav>
          {/* Mid-flow: show the help contact. On landing/info pages: prompt to start enrolling. */}
          {isInFlow ? <ConciergeContact /> : <EnrollCta base={base} />}
          <button
            type='button'
            className='ml-1 inline-flex h-9 w-9 items-center justify-center rounded-lg text-neutral-600 transition-colors hover:bg-neutral-100 md:hidden'
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((open) => !open)}
          >
            {menuOpen ? <X className='h-5 w-5' /> : <Menu className='h-5 w-5' />}
          </button>
        </div>
      </div>

      {/* Mobile dropdown (below md), toggled by the hamburger above. */}
      {menuOpen && (
        <nav className='mx-auto max-w-7xl px-6 pb-2 md:hidden'>
          <NavLinks base={base} mobile onNavigate={() => setMenuOpen(false)} />
        </nav>
      )}
    </header>
  );
}

function EnrollCta({base}: {base: string}) {
  return (
    <Link
      to={`${base}/medical`}
      className='ml-1 inline-flex h-9 shrink-0 items-center gap-1.5 rounded-lg bg-[var(--enroll-accent)] px-4 text-sm font-semibold text-white transition-colors hover:bg-[var(--enroll-accent-hover)]'
    >
      <span>Enroll<span className='hidden sm:inline'> Now</span></span>
      <ArrowRight className='hidden h-3.5 w-3.5 sm:inline' />
    </Link>
  );
}

function ConciergeContact() {
  const group = useEnrollGroup();
  const phoneDigits = group.supportPhone.replace(/[^\d]/g, '');
  return (
    <a
      href={`tel:${phoneDigits}`}
      className='ml-1 flex items-center gap-2 text-sm text-neutral-700 transition-colors hover:text-[var(--enroll-ink)]'
      aria-label={`Call the Concierge Team at ${group.supportPhone}`}
    >
      <Phone className='h-4 w-4 shrink-0' />
      <span className='hidden leading-tight sm:block'>
        <span className='block text-[10px] uppercase tracking-wider text-neutral-400'>
          Concierge Team
        </span>
        <span className='block font-semibold'>{group.supportPhone}</span>
      </span>
    </a>
  );
}
